/**
 * Rough heuristic estimate of the output GIF size for given conversion settings.
 * Unlike the image converter's estimate (a real `canvas.toBlob` encode, exact for
 * the chosen settings), an actual encode here would mean running the full
 * server-side ffmpeg palette pipeline just to preview a number — expensive,
 * rate-limited compute this tool deliberately protects (see converterLimits.ts).
 *
 * A single-factor point estimate was tried first (0.4× the raw indexed-frame
 * size) and was ~3x too high against a real conversion (predicted 138 MB,
 * actual output 46.1 MB) — GIF LZW compresses ffmpeg's palette-based/dithered
 * frames far better than that in practice, and the "right" factor varies a lot
 * with content (a mostly-static screen recording compresses much better than
 * high-motion footage). Rather than chase a single constant that will always be
 * wrong for *some* content, this returns a MIN/MAX range spanning that variance
 * — honest about the uncertainty instead of presenting a falsely precise number.
 */
const LOW_COMPRESSION_FACTOR = 0.07 // mostly-static content (UI/screen recordings, slow pans)
const HIGH_COMPRESSION_FACTOR = 0.22 // high-motion/noisy content compresses worse
const GIF_CONTAINER_OVERHEAD_BYTES = 800

export interface EstimateGifSizeParams {
  outputWidthPx: number
  outputHeightPx: number
  fps: number
  durationSec: number
  colors: number
}

export interface EstimatedGifSizeRange {
  minBytes: number
  maxBytes: number
}

export const estimateGifSizeRange = ({
  outputWidthPx,
  outputHeightPx,
  fps,
  durationSec,
  colors,
}: EstimateGifSizeParams): EstimatedGifSizeRange => {
  const bitsPerPixel = Math.log2(Math.max(2, colors))
  const totalFrames = Math.max(1, Math.round(fps * durationSec))
  const rawBytesPerFrame = (outputWidthPx * outputHeightPx * bitsPerPixel) / 8

  return {
    minBytes: Math.round(rawBytesPerFrame * LOW_COMPRESSION_FACTOR * totalFrames + GIF_CONTAINER_OVERHEAD_BYTES),
    maxBytes: Math.round(rawBytesPerFrame * HIGH_COMPRESSION_FACTOR * totalFrames + GIF_CONTAINER_OVERHEAD_BYTES),
  }
}
